<template>
  <el-card 
    class="route-card card-hover" 
    :body-style="{ padding: '0' }"
    @click="$emit('click')"
  >
    <!-- 卡片头部 -->
    <div class="card-header">
      <div class="route-image" :style="{ backgroundImage: `url(${routeImage})` }">
        <div class="image-overlay">
          <div class="route-title">
            <h3>{{ route.name }}</h3>
            <el-tag :type="getThemeType(route.theme)" effect="dark" size="small">
              {{ route.theme }}
            </el-tag>
          </div>
          <div class="route-rating">
            <span class="rating-score">{{ route.rating }}</span>
            <el-rate 
              :model-value="parseFloat(route.rating)" 
              disabled 
              show-score 
              text-color="#ff9900"
              score-template=""
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 卡片内容 -->
    <div class="card-body">
      <div class="stats-row">
        <div class="stat-item">
          <el-icon><MapLocation /></el-icon>
          <span class="stat-value">{{ route.totalDistance }}km</span>
          <span class="stat-label">总里程</span>
        </div>
        <div class="stat-item">
          <el-icon><Timer /></el-icon>
          <span class="stat-value">{{ route.totalDuration }}h</span>
          <span class="stat-label">用时</span>
        </div>
        <div class="stat-item">
          <el-icon><Money /></el-icon>
          <span class="stat-value" style="color: #FF6B35; font-weight: 600;">¥{{ route.budgetDetails?.total?.toLocaleString() }}</span>
          <span class="stat-label">预算</span>
        </div>
        <div class="stat-item">
          <el-icon><LocationInformation /></el-icon>
          <span class="stat-value">{{ route.pois?.length }}</span>
          <span class="stat-label">景点</span>
        </div>
      </div>

      <!-- 景点预览 -->
      <div class="pois-preview">
        <div 
          v-for="(poi, index) in route.pois?.slice(0, 4)" 
          :key="index"
          class="poi-tag"
        >
          <span class="poi-number">{{ index + 1 }}</span>
          <span class="poi-name">{{ poi.name }}</span>
        </div>
        <div v-if="route.pois?.length > 4" class="poi-tag more">
          +{{ route.pois.length - 4 }}
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="card-actions">
        <el-button 
          type="primary" 
          size="large"
          @click.stop="$emit('view-detail')"
          class="detail-btn"
        >
          <el-icon><View /></el-icon>
          查看详情
        </el-button>
        
        <el-button 
          size="large"
          @click.stop="copyShareLink"
          class="share-btn"
        >
          <el-icon><Share /></el-icon>
          分享
        </el-button>
      </div>
    </div>
  </el-card>
</template>

<script setup>
import { computed } from 'vue'
import { MapLocation, Timer, Money, LocationInformation, View, Share } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const props = defineProps({
  route: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['click', 'view-detail'])

const routeImage = computed(() => {
  // Use themed images based on route theme
  const themeImages = {
    '亲子': 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800',
    '情侣': 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=800',
    '摄影': 'https://images.unsplash.com/photo-1452421822248-d4c2b47f0c81?w=800',
    '美食': 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800',
    '山水': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800'
  }
  return themeImages[props.route.theme] || themeImages['山水']
})

const getThemeType = (theme) => {
  const types = {
    '亲子': 'success',
    '情侣': 'danger',
    '摄影': 'primary',
    '美食': 'warning',
    '山水': 'info'
  }
  return types[theme] || 'info'
}

const copyShareLink = () => {
  if (props.route.shareUrl) {
    navigator.clipboard.writeText(props.route.shareUrl)
    ElMessage.success('分享链接已复制到剪贴板！')
  } else {
    ElMessage.warning('分享链接暂不可用')
  }
}
</script>

<style scoped>
.route-card {
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.route-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.card-header {
  position: relative;
}

.route-image {
  height: 200px;
  background-size: cover;
  background-position: center;
  position: relative;
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  color: white;
}

.route-title h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
}

.route-rating {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rating-score {
  font-size: 24px;
  font-weight: bold;
  color: #ff9900;
}

.card-body {
  padding: 20px;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 10px;
}

.stat-item .el-icon {
  font-size: 24px;
  color: #00B5FF;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.stat-label {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.pois-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.poi-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f0f7ff;
  border-radius: 20px;
  font-size: 13px;
}

.poi-tag .poi-number {
  width: 20px;
  height: 20px;
  background: #00B5FF;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
}

.poi-tag.more {
  background: #e9ecef;
  color: #666;
}

.card-actions {
  display: flex;
  gap: 10px;
}

.detail-btn {
  flex: 1;
  background: linear-gradient(135deg, #00B5FF 0%, #0077FF 100%);
  border: none;
  border-radius: 25px;
}

.share-btn {
  flex: 0 0 auto;
  border-radius: 25px;
}

@media (max-width: 768px) {
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>